import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { useShopModuleIdTypePairs } from '../../../store/selectors/shopModulesSelector';
import { useServiceModuleIdNamePairs } from '../../../store/selectors/serviceModulesSelector';
import { useActionModuleIdNamePairs } from '../../../store/selectors/actionModulesSelectors';
import { fetchShopModulesThunk } from '../../../store/thunk/shopModulesThunk';
import { fetchServiceModulesThunk } from '../../../store/thunk/serviceModulesThunk';
import { fetchActionModules } from '../../../store/thunk/actionsModulesThunk';
import { useEmployeeActionModules, useEmployeeServiceModules, useEmployeeShopModules } from '../../../store/selectors/employeesSelectors';
import { fetchEmployeeActionModulesThunk, fetchEmployeeServiceModulesThunk, fetchEmployeeShopModulesThunk, patchEmployeeActionModulesThunk, patchEmployeeServiceModulesThunk, patchEmployeeShopModulesThunk } from '../../../store/thunk/employeesThunk';
import { Checkbox, Dialog, DialogContent } from '@mui/material';
import { AssignedText, CheckboxListTile, DialogActionsWrapper, ModuleCategoryCard, ModuleCategoryTitle, ModuleList, ModulesDialogWrapper, ModuleSectionsContainer, ModulesTitle } from '../Employees.styles';
import { useTranslation } from 'react-i18next';
import { GeneralButton } from '../../../common/ui/GeneralButton';

interface EmployeeModulesDialogProps {
  userId: string;
  userName: string;
  open: boolean;
  onClose: () => void;
}

const areArraysEqual = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
};

const EmployeeModulesDialog: React.FC<EmployeeModulesDialogProps> = ({ userId, userName, open, onClose }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const employeeActionModuleIds = useEmployeeActionModules(userId);
  const employeeServiceModuleIds = useEmployeeServiceModules(userId);
  const employeeShopModuleIds = useEmployeeShopModules(userId);
  const allActionModulePairs = useActionModuleIdNamePairs();
  const allServiceModulePairs = useServiceModuleIdNamePairs();
  const allShopModulePairs = useShopModuleIdTypePairs();
  const [checkedActionIds, setCheckedActionIds] = useState<number[]>([]);
  const [checkedServiceIds, setCheckedServiceIds] = useState<number[]>([]);
  const [checkedShopIds, setCheckedShopIds] = useState<number[]>([]);
  const [originalActionIds, setOriginalActionIds] = useState<number[]>([]);
  const [originalServiceIds, setOriginalServiceIds] = useState<number[]>([]);
  const [originalShopIds, setOriginalShopIds] = useState<number[]>([]);

  useEffect(() => {
    dispatch(fetchEmployeeActionModulesThunk(userId));
    dispatch(fetchEmployeeServiceModulesThunk(userId));
    dispatch(fetchEmployeeShopModulesThunk(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    setCheckedActionIds(employeeActionModuleIds);
    setCheckedServiceIds(employeeServiceModuleIds);
    setCheckedShopIds(employeeShopModuleIds);
    setOriginalActionIds(employeeActionModuleIds);
    setOriginalServiceIds(employeeServiceModuleIds);
    setOriginalShopIds(employeeShopModuleIds);
  }, [employeeActionModuleIds, employeeServiceModuleIds, employeeShopModuleIds]);

  useEffect(() => {
    if (allActionModulePairs.length === 0) dispatch(fetchActionModules())
    if (allServiceModulePairs.length === 0) dispatch(fetchServiceModulesThunk())
    if (allShopModulePairs.length === 0) dispatch(fetchShopModulesThunk());
  }, [dispatch, allActionModulePairs.length, allServiceModulePairs.length, allShopModulePairs.length]);

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  const resetState = () => {
    setCheckedActionIds(originalActionIds);
    setCheckedServiceIds(originalServiceIds);
    setCheckedShopIds(originalShopIds);
  };

  const hasChanges =
    !areArraysEqual(checkedActionIds, originalActionIds) ||
    !areArraysEqual(checkedServiceIds, originalServiceIds) ||
    !areArraysEqual(checkedShopIds, originalShopIds);

  const handleToggleCheckbox = (category: 'action' | 'service' | 'shop', id: number) => {
    const updaterMap: Record<'action' | 'service' | 'shop', [number[], React.Dispatch<React.SetStateAction<number[]>>]> = {
      action: [checkedActionIds, setCheckedActionIds],
      service: [checkedServiceIds, setCheckedServiceIds],
      shop: [checkedShopIds, setCheckedShopIds],
    };

    const [, setCurrent] = updaterMap[category];
    setCurrent((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleModuleListUpdate = async () => {
    await Promise.all([
      dispatch(patchEmployeeActionModulesThunk({ employeeId: userId, moduleIds: checkedActionIds })),
      dispatch(patchEmployeeServiceModulesThunk({ employeeId: userId, moduleIds: checkedServiceIds })),
      dispatch(patchEmployeeShopModulesThunk({ employeeId: userId, moduleIds: checkedShopIds })),
    ]);

    await Promise.all([
      dispatch(fetchEmployeeActionModulesThunk(userId)),
      dispatch(fetchEmployeeServiceModulesThunk(userId)),
      dispatch(fetchEmployeeShopModulesThunk(userId)),
    ]);

    setOriginalActionIds(checkedActionIds);
    setOriginalServiceIds(checkedServiceIds);
    setOriginalShopIds(checkedShopIds);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogContent sx={{ height: "90vh" }}>
        <ModulesDialogWrapper>
          <ModulesTitle>{t("employeesPage.moduleAssignment.assignedModules", { name: userName })}</ModulesTitle>

          <ModuleSectionsContainer>
            <ModuleCategoryCard>
              <ModuleCategoryTitle>{t("actionModulesPage.actionModules.title")}</ModuleCategoryTitle>
              <ModuleList>
                {
                  allActionModulePairs.length === 0 ? (
                    <AssignedText>{t("employeesPage.moduleAssignment.noModulesAvailable")}</AssignedText>
                  ) : (
                    allActionModulePairs.map(({ name, id }) => (
                      <CheckboxListTile
                        control={
                          <Checkbox
                            checked={checkedActionIds.includes(id)}
                            onChange={() => handleToggleCheckbox('action', id)}
                          />
                        }
                        label={name}
                      />
                    ))
                  )
                }
              </ModuleList>
            </ModuleCategoryCard>

            <ModuleCategoryCard>
              <ModuleCategoryTitle>{t("serviceModulesPage.serviceModules")}</ModuleCategoryTitle>
              <ModuleList>
                {
                  allServiceModulePairs.length === 0 ? (
                    <AssignedText>{t("employeesPage.moduleAssignment.noModulesAvailable")}</AssignedText>
                  ) : (
                    allServiceModulePairs.map(({ name, id }) => (
                      <CheckboxListTile
                        control={
                          <Checkbox
                            checked={checkedServiceIds.includes(id)}
                            onChange={() => handleToggleCheckbox('service', id)}
                          />
                        }
                        label={name}
                      />
                    ))
                  )
                }
              </ModuleList>
            </ModuleCategoryCard>

            <ModuleCategoryCard>
              <ModuleCategoryTitle>{t("shopModulesPage.shopModules")}</ModuleCategoryTitle>
              <ModuleList>
                {
                  allShopModulePairs.length === 0 ? (
                    <AssignedText>{t("employeesPage.moduleAssignment.noModulesAvailable")}</AssignedText>
                  ) : (
                    allShopModulePairs.map(({ type, id }) => (
                      <CheckboxListTile
                        control={
                          <Checkbox
                            checked={checkedShopIds.includes(id)}
                            onChange={() => handleToggleCheckbox('shop', id)}
                          />
                        }
                        label={type}
                      />
                    ))
                  )
                }
              </ModuleList>
            </ModuleCategoryCard>
          </ModuleSectionsContainer>

          <DialogActionsWrapper>
            <GeneralButton
              appVariant="secondary"
              onClick={onClose}
            >
              {t("buttons.close")}
            </GeneralButton>
            <GeneralButton
              onClick={handleModuleListUpdate}
              disabled={!hasChanges}
            >
              {t("buttons.save")}
            </GeneralButton>
          </DialogActionsWrapper>
        </ModulesDialogWrapper>
      </DialogContent>
    </Dialog >
  );
};

export default EmployeeModulesDialog;